








































































































































import countryCodes from 'highered-utils/data/codes.json';
import countries from 'highered-utils/data/countries.json';
import parsePhoneNumber from 'libphonenumber-js';
import Vue, { PropType } from 'vue';
import Avatar from '~/components/builders/Avatar.vue';
import Input from '~/components/builders/Base/Input.vue';
import Select, { SelectOption } from '~/components/builders/Base/Select.vue';
import { Gender, PronounType } from '~/services/CandidateService';
import { CandidateJobBuilder } from '~/utils/candidate/CandidateJobBuilder';

export default Vue.extend({
    name: 'PersonalOverviewForm',
    components: {
        Input,
        Select,
        Avatar,
    },
    props: {
        builder: {
            type: Object as PropType<CandidateJobBuilder>,
            required: true,
        },
    },
    data() {
        return {
            avatar: this.builder.getUserField('avatar') || '',
            fname: this.builder.getUserField('firstName') || '',
            lname: this.builder.getUserField('lastName') || '',
            gender: this.builder.getCandidateField('gender') || '',
            pronoun: this.builder.getCandidateField('pronoun'),
            email: this.builder.getUserField('email') || '',
            ccode: '',
            phone: '',

            nationalities: this.builder.getNationalities(),
            country: this.builder.getCandidateField('country'),
            workAuths: this.builder.getWorkAuthorizations(),
        };
    },
    mounted() {
        this.initPhoneNumber();

        this.$watch('fname', this.builder.watchUser('firstName'));
        this.$watch('lname', this.builder.watchUser('lastName'));
        this.$watch('fullPhoneNumber', this.builder.watchUser('phoneNumber'));

        this.$watch('gender', this.builder.watchCandidate('gender'));
        this.$watch('pronoun', this.builder.watchCandidate('pronoun'));
        this.$watch('country', this.builder.watchCandidate('country'));

        this.$watch('workAuths', this.builder.watchWorkAuthorizations());
        this.$watch('nationalities', this.builder.watchNationalities());
    },
    computed: {
        pronounValue: {
            get(): string {
                return this.pronoun ? this.pronoun.type : '';
            },
            set(value: string) {
                const found = this.optionsPronoun.find(p => p.value === value);
                if (!found) return;
                this.pronoun = {
                    type: found.value,
                    value: '',
                };
            },
        },

        optionsGender(): SelectOption<Gender>[] {
            const t = (id: Gender) =>
                this.$t(`profile.personalDetails.genderOptions.${id}`).toString();

            return [
                { value: 'M', label: t('M') },
                { value: 'F', label: t('F') },
                { value: 'B', label: t('B') },
                { value: 'O', label: t('O') },
                { value: 'N', label: t('N') },
            ];
        },
        optionsPronoun(): SelectOption<PronounType>[] {
            const t = (id: PronounType) =>
                this.$t(`profile.personalDetails.pronounOptions.${id}`).toString();

            return [
                { value: 'he/him', label: t('he/him') },
                { value: 'she/her', label: t('she/her') },
                { value: 'they/them', label: t('they/them') },
                { value: 'other', label: t('other') },
                { value: 'prefer_not_to_say', label: t('prefer_not_to_say') },
            ];
        },
        optionsCountryCodes(): SelectOption<string, string>[] {
            return countryCodes.map(c => ({
                value: c.value,
                label: `${c.prefix} ${c.label}`,
                suffix: c.suffix,
                search: c.suffix,
                extra: c.label,
            }));
        },
        optionsCountries(): SelectOption[] {
            return countries.map(c => ({
                value: c.value,
                label: `${c.prefix} ${c.label}`,
                search: c.label,
            }));
        },
        optionsWorkAuthorizations(): SelectOption[] {
            const workAuths = [...this.optionsCountries];
            workAuths.unshift({
                value: 'EU',
                label: '🇪🇺 EU',
                search: 'EU',
            });
            return workAuths;
        },

        noAvatarSuffix(): boolean {
            return this.avatar.startsWith('blob:');
        },
        fullPhoneNumber(): string {
            if (!this.phone) return '';

            const code = this.optionsCountryCodes.find(o => o.value === this.ccode);
            if (!code || !code.extra) return this.phone;
            return `${code.extra} ${this.phone}`;
        },
    },
    methods: {
        initPhoneNumber() {
            const phone = this.builder.getUserField('phoneNumber') || '';
            if (!phone) return;

            let nr = parsePhoneNumber(phone);
            if (!nr) {
                // The number might not include the country code, so we try to parse it as a Norwegian number and exclude the country code
                nr = parsePhoneNumber(phone, 'NO');
                if (!nr) {
                    return;
                }
                this.phone = nr.nationalNumber;
                return;
            }

            const { countryCallingCode, nationalNumber } = nr;
            const cc = countryCodes.find(c => c.label === `+${countryCallingCode}`);
            if (!!cc) this.ccode = cc.value;
            this.phone = nationalNumber;
        },

        handleAvatarChange(files: FileList) {
            if (!files.length) return;

            const imageFile = files[0];
            if (!imageFile.type.match('image.*')) {
                window.alert(this.$t('profile.personalDetails.errors.wrongFileType').toString());
                return;
            }

            this.avatar = URL.createObjectURL(imageFile);
            this.$emit('avatarChange', imageFile);
        },

        handleAddNationality() {
            this.nationalities.push({
                id: CandidateJobBuilder.generateId(),
                nationality: '',
            });
        },
        handleChangeNationality(idx: number, nationality: string) {
            const natio = this.nationalities[idx];
            const natios = [...this.nationalities];
            natios[idx] = {
                ...natio,
                nationality,
            };
            this.nationalities = natios;
        },
        handleRemoveNationality(idx: number) {
            const natios = [...this.nationalities];
            natios.splice(idx, 1);
            this.nationalities = natios;
        },

        handleAddAuth() {
            this.workAuths.push({
                id: CandidateJobBuilder.generateId(),
                code: '',
                type: 0,
            });
        },
        handleChangeAuth(idx: number, code: string) {
            if (this.workAuths.some(wa => wa.code === code)) return;

            const auth = this.workAuths[idx];
            const auths = [...this.workAuths];

            auths[idx] = {
                ...auth,
                code,
                type: code !== 'EU' ? 0 : 1,
            };

            this.workAuths = auths;
        },
        handleRemoveAuth(idx: number) {
            const auths = [...this.workAuths];
            auths.splice(idx, 1);
            this.workAuths = auths;
        },
    },
});
